import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { CREATE_VENUE } from "./Venue";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import Loading from "../common/Loading";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const INITIAL_LOCATION = {
    address: "",
    city: "",
    state: "",
    country: "",
    latitude: "",
    longitude: "",
    placeId: "",
};

const INITIAL_FIELD = {
    name: "",
    description: "",
};

export default function VenueCreateModal({ onCreated, onClose, show }) {
    const scopeId = "kbola-venue-create-modal";
    const colors = useThemeColors();
    const { t } = useTranslation();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const [searchResult, setSearchResult] = useState(null);
    const fieldAddressInput = useRef(null);

    const [uploading, setUploading] = useState(false);
    const [location, setLocation] = useState(INITIAL_LOCATION);
    const [fields, setFields] = useState([{ ...INITIAL_FIELD }]);

    const resetForm = () => {
        setName("");
        setDescription("");
        setSearchResult(null);
        setLocation(INITIAL_LOCATION);
        setFields([{ ...INITIAL_FIELD }]);

        if (fieldAddressInput.current) {
            fieldAddressInput.current.value = "";
        }
    };

    useEffect(() => {
        if (!show) return;

        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = prev || "";
        };
    }, [show]);

    const ui = useMemo(() => {
        return {
            backdrop: {
                backgroundColor: "rgba(0,0,0,0.55)",
            },
            dialog: {
                maxWidth: 820,
            },
            content: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },

            header: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.5)},
                    ${alphaFromHex(colors.primary, 0)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                zIndex: 3,
            },
            footer: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(255,255,255,0.96)",
                backdropFilter: "blur(2px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                position: "sticky",
                bottom: 0,
                zIndex: 3,
            },

            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: "#14151a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
            },

            body: {
                padding: 14,
                background: "#fff",
            },

            sectionCard: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            sectionTitle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
            },
            sectionTitleText: {
                margin: 0,
                fontWeight: 1000,
                fontSize: 13,
                color: "rgba(20,21,26,0.85)",
            },

            label: {
                fontWeight: 900,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                fontWeight: 800,
            },
            textarea: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                fontWeight: 800,
            },

            addFieldBtn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: colors.secondary,
                color: colors.textOnSecondary,
                cursor: "pointer",
            },
            btnGhost: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
            },
            btnDangerGhost: {
                borderRadius: 12,
                padding: "8px 10px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                border: "1px solid rgba(180,35,24,0.25)",
                background: "#fff",
                color: "#b42318",
                cursor: "pointer",
            },
            btnPrimary: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.10)",
                background: GlobalStyles.colors.canary,
                color: GlobalStyles.colors.woodsmoke,
                cursor: "pointer",
            },

            fieldRow: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "#fff",
            },
            fieldRowHeader: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
            },
            fieldIndexBadge: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                fontWeight: 1000,
                color: "rgba(20,21,26,0.7)",
            },
            fieldMeta: {
                fontWeight: 900,
                fontSize: 12,
                color: "rgba(20,21,26,0.55)",
            },
        };
    }, [colors.primary, colors.secondary, colors.textOnPrimary, colors.textOnSecondary]);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: ["places"],
    });

    const [createVenue, { error }] = useMutation(CREATE_VENUE, {
        onError: (err) => console.error(err),
    });

    const handleClose = () => {
        if (uploading) return;

        onClose?.();
        resetForm();
    };

    const handleAddField = (e) => {
        e.preventDefault();

        setFields((prev) => [...prev, { ...INITIAL_FIELD }]);
    };

    const handleRemoveField = (index) => {
        setFields((prev) => {
            if (prev.length <= 1) {
                return [{ ...INITIAL_FIELD }];
            }

            return prev.filter((_, i) => i !== index);
        });
    };

    const handleFieldChange = (index, key, value) => {
        setFields((prev) => {
            const next = [...prev];

            next[index] = {
                ...next[index],
                [key]: value,
            };

            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);

        if (!name.trim()) {
            toast.error(t("venueCreateModal.venueNameRequired"));
            setUploading(false);
            return;
        }

        if (!location.address) {
            toast.error(t("venueCreateModal.addressRequired") || "Please select a valid address.");
            setUploading(false);
            return;
        }

        const cleanedFields = fields
            .map((f) => ({
                name: f.name?.trim() || "",
                description: f.description?.trim() || "",
            }))
            .filter((f) => f.name !== "");

        if (cleanedFields.length === 0) {
            toast.error(t("venueCreateModal.validationAtLeastOneField"));
            setUploading(false);
            return;
        }

        try {
            const input = {
                name: name.trim(),
                description: description?.trim() || "",
                location: {
                    address: location.address || "",
                    city: location.city || "",
                    state: location.state || "",
                    country: location.country || "",
                    latitude: parseFloat(location.latitude) || null,
                    longitude: parseFloat(location.longitude) || null,
                    placeId: location.placeId || "",
                },
                fields: cleanedFields,
            };

            const { data } = await createVenue({
                variables: {
                    input,
                },
            });

            if (data?.createVenue?.venue) {
                onCreated?.(data.createVenue.venue);
                toast.success(t("venueCreateModal.createSuccess"));
                onClose?.();
                resetForm();
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "venueCreateModal.createFailed"));
        } finally {
            setUploading(false);
        }
    };

    const onLoad = (autocomplete) => {
        setSearchResult(autocomplete);
    };

    const onPlaceChanged = () => {
        if (!searchResult) return;

        const place = searchResult.getPlace();

        const locationObj = {
            address: place.formatted_address || "",
            city: "",
            state: "",
            country: "",
            latitude: place.geometry?.location?.lat?.() ?? "",
            longitude: place.geometry?.location?.lng?.() ?? "",
            placeId: place.place_id || "",
        };

        (place.address_components || []).forEach((component) => {
            const types = component.types || [];

            if (types.includes("locality")) {
                locationObj.city = component.long_name;
            }

            if (types.includes("administrative_area_level_1")) {
                locationObj.state = component.long_name;
            }

            if (types.includes("country")) {
                locationObj.country = component.long_name;
            }
        });

        setLocation(locationObj);
    };

    if (!isLoaded) return <Loading />;

    return (
        <div
            id={scopeId}
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex="-1"
            role="dialog"
            aria-hidden={!show}
            style={ui.backdrop}
        >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document" style={ui.dialog}>
                <div className="modal-content" style={ui.content}>
                    <Form method="post" onSubmit={handleSubmit}>
                        <div style={ui.header}>
                            <div style={ui.titleWrap}>
                                <span style={ui.badge}>{t("venueCreateModal.badge")}</span>

                                <div style={{ minWidth: 0 }}>
                                    <h5 style={ui.title}>{t("venueCreateModal.title")}</h5>
                                    <p style={ui.sub}>{t("venueCreateModal.subtitle")}</p>
                                </div>
                            </div>

                            <button
                                type="button"
                                style={{
                                    ...ui.closeBtn,
                                    opacity: uploading ? 0.7 : 1,
                                }}
                                onClick={handleClose}
                                title={t("venueCreateModal.close")}
                                aria-label={t("venueCreateModal.close")}
                                disabled={uploading}
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>

                        <div className="modal-body" style={ui.body}>
                            {error ? <div className="alert alert-danger">{getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}</div> : null}

                            <div className="form-group">
                                <label style={ui.label}>{t("venueCreateModal.venueName")}</label>

                                <input
                                    className="form-control"
                                    style={ui.input}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={t("venueCreateModal.venueNamePlaceholder")}
                                    required
                                    disabled={uploading}
                                />
                            </div>

                            <div className="form-group">
                                <label style={ui.label}>{t("venueCreateModal.description")}</label>

                                <textarea
                                    className="form-control"
                                    style={ui.textarea}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t("venueCreateModal.descriptionPlaceholder")}
                                    rows={3}
                                    disabled={uploading}
                                />
                            </div>

                            <div className="form-group">
                                <label style={ui.label}>{t("venueCreateModal.address")}</label>

                                <Autocomplete onPlaceChanged={onPlaceChanged} onLoad={onLoad}>
                                    <input
                                        required
                                        className="form-control"
                                        style={ui.input}
                                        type="text"
                                        placeholder={t("venueCreateModal.addressPlaceholder")}
                                        ref={fieldAddressInput}
                                        disabled={uploading}
                                    />
                                </Autocomplete>

                                {location?.address ? (
                                    <div className="mt-2" style={ui.sectionCard}>
                                        <div style={ui.fieldMeta}>
                                            <i className="fas fa-map-marker-alt mr-2" />
                                            {location.address}
                                        </div>

                                        <div style={{ ...ui.fieldMeta, marginTop: 6 }}>
                                            {location.city ? `${location.city}, ` : ""}
                                            {location.state ? `${location.state}, ` : ""}
                                            {location.country || ""}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div className="mt-3" style={ui.sectionCard}>
                                <div style={ui.sectionTitle}>
                                    <h6 style={ui.sectionTitleText}>{t("venueCreateModal.fieldsTitle")}</h6>

                                    <span style={{ ...ui.fieldMeta, margin: 0 }}>
                                        {t("venueCreateModal.itemsCount", { count: fields.length })}
                                    </span>
                                </div>

                                {fields.map((field, index) => (
                                    <div key={`venue-field-${index}`} style={{ ...ui.fieldRow, marginBottom: 10 }}>
                                        <div style={ui.fieldRowHeader}>
                                            <div style={ui.fieldIndexBadge}>
                                                <i className="fas fa-map-marked-alt" />
                                                <span>
                                                    {t("venueCreateModal.fieldLabel", {
                                                        number: index + 1,
                                                    }) || `Field ${index + 1}`}
                                                </span>
                                            </div>

                                            <button
                                                type="button"
                                                style={{
                                                    ...ui.btnDangerGhost,
                                                    opacity: uploading ? 0.7 : 1,
                                                }}
                                                onClick={() => handleRemoveField(index)}
                                                disabled={uploading}
                                                title={t("venueCreateModal.removeField") || "Remove field"}
                                            >
                                                <i className="fas fa-trash" />
                                                <span className="d-none d-sm-inline">
                                                    {t("venueCreateModal.removeField") || "Remove"}
                                                </span>
                                            </button>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-5">
                                                <div className="form-group mb-2">
                                                    <label style={ui.label}>{t("venueCreateModal.fieldName")}</label>

                                                    <input
                                                        className="form-control"
                                                        style={ui.input}
                                                        placeholder={t("venueCreateModal.fieldNamePlaceholder")}
                                                        value={field.name}
                                                        onChange={(e) =>
                                                            handleFieldChange(index, "name", e.target.value)
                                                        }
                                                        required={index === 0}
                                                        disabled={uploading}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-7">
                                                <div className="form-group mb-2">
                                                    <label style={ui.label}>
                                                        {t("venueCreateModal.fieldDescription")}
                                                    </label>

                                                    <input
                                                        className="form-control"
                                                        style={ui.input}
                                                        placeholder={t("venueCreateModal.fieldDescriptionPlaceholder")}
                                                        value={field.description}
                                                        onChange={(e) =>
                                                            handleFieldChange(index, "description", e.target.value)
                                                        }
                                                        disabled={uploading}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    style={{
                                        ...ui.addFieldBtn,
                                        opacity: uploading ? 0.7 : 1,
                                    }}
                                    onClick={handleAddField}
                                    disabled={uploading}
                                    title={t("venueCreateModal.addAnotherFieldTitle")}
                                >
                                    <i className="fas fa-plus" />
                                    {t("venueCreateModal.addAnotherField")}
                                </button>
                            </div>
                        </div>

                        <div className="modal-footer" style={ui.footer}>
                            <button
                                type="button"
                                style={{
                                    ...ui.btnGhost,
                                    opacity: uploading ? 0.7 : 1,
                                }}
                                onClick={handleClose}
                                disabled={uploading}
                            >
                                {t("venueCreateModal.cancel")}
                            </button>

                            <button
                                type="submit"
                                style={{
                                    ...ui.btnPrimary,
                                    opacity: uploading ? 0.8 : 1,
                                }}
                                disabled={uploading}
                            >
                                {uploading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        {t("venueCreateModal.creating")}
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-check" />
                                        {t("venueCreateModal.createVenue")}
                                    </>
                                )}
                            </button>
                        </div>
                    </Form>
                </div>
            </div>

            <style>{`
                #${scopeId} {
                    font-family: 'Poppins', sans-serif;
                }

                #${scopeId} .modal-dialog {
                    margin: 1.25rem auto;
                }

                #${scopeId} .modal-body {
                    max-height: calc(100vh - 220px);
                    overflow-y: auto;
                    -webkit-overflow-scrolling: touch;
                }

                #${scopeId} .form-control:focus,
                #${scopeId} button:focus {
                    outline: none;
                    box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                    border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
                }

                #${scopeId} button:disabled {
                    cursor: not-allowed;
                }

                #${scopeId} i.fas,
                #${scopeId} i.far,
                #${scopeId} i.fal,
                #${scopeId} i.fa,
                #${scopeId} i[class^="fa-"],
                #${scopeId} i[class*=" fa-"] {
                    font-family: "Font Awesome 5 Free" !important;
                    font-weight: 900;
                }

                #${scopeId} i.fab {
                    font-family: "Font Awesome 5 Brands" !important;
                    font-weight: 400;
                }
            `}</style>
        </div>
    );
}
